import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  Container,
  Header,
  NavButton,
  TitleDot,
  TitlePart,
  TitleWrapper,
  DayWrapper,
  CalendarItem,
  Day,
  TodayButton,
  DayCircle,
} from "../components/calendarStyles";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const weeks = [
  "일요일",
  "월요일",
  "화요일",
  "수요일",
  "목요일",
  "금요일",
  "토요일",
];

const Schedule = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isAdmin = sessionStorage.getItem("isAdmin") === "true";
  // const isAdmin = "true";
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("start-9");
  const [endTime, setEndTime] = useState("end-10");
  const [content, setContent] = useState("");
  const [events, setEvents] = useState([]); // 화면에 표시할 일정 목록
  const [allDay, setAllDay] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // 종일 버튼 핸들러
  const toggleAllDay = () => {
    setAllDay((prev) => !prev);
  };
  const parseTimeValue = (value) => {
    // "start-9" → "09:00"
    const hour = value.split("-")[1];
    return `${hour.toString().padStart(2, "0")}:00`;
  };

  const handleSubmitEvent = async () => {
    if (!startDate || !endDate || !content) {
      Swal.fire({
        icon: "warning",
        title: "입력 오류",
        text: "모든 필수 정보를 입력해주세요.",
      });
      return;
    }
    const parsedStartTime = parseTimeValue(startTime); // "09:00"
    const parsedEndTime = parseTimeValue(endTime); // "10:00"

    const startDateTime = `${startDate}T${parsedStartTime}`;
    const endDateTime = `${endDate}T${parsedEndTime}`;

    if (dayjs(endDateTime).isBefore(dayjs(startDateTime))) {
      Swal.fire({
        icon: "error",
        title: "시간 설정 오류",
        text: "시작 시간은 종료 시간보다 빠르거나 같아야 합니다.",
      });
      return;
    }
    try {
      const response = selectedEventId
        ? await axios.put(
          `${baseUrl}/api/schedules/update/${selectedEventId}`,
          {
            startDateTime,
            endDateTime,
            content,
            allDay,
          }
        )
        : await axios.post(`${baseUrl}/api/schedules/save`, {
          startDateTime,
          endDateTime,
          content,
          allDay,
        });

      const status = response.data.status;

      if (status === "duplicate") {
        Swal.fire({
          icon: "warning",
          title: "중복 일정",
          text: response.data.message,
          confirmButtonColor: "#d33",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: selectedEventId ? "수정 완료" : "등록 완료",
      });

      setIsModalOpen(false);
      resetForm();
      fetchEvents();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "오류 발생",
        text: "서버와의 통신 중 문제가 발생했습니다.",
      });
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetForm = () => {
    setStartDate(dayjs().format("YYYY-MM-DD"));
    setEndDate(dayjs().format("YYYY-MM-DD"));
    setStartTime("start-9");
    setEndTime("end-10");
    setContent("");
    setAllDay(false);
    setSelectedEventId(null);
  };

  const fetchEvents = async () => {
    const start = dayjs(currentDate)
      .startOf("month")
      .format("YYYY-MM-DDT00:00:00");
    const end = dayjs(currentDate).endOf("month").format("YYYY-MM-DDT23:59:59");

    try {
      const res = await axios.get(
        `${baseUrl}/api/schedules/list?start=${start}&end=${end}`
      );
      setEvents(res.data);
    } catch (err) {
      console.error("일정 불러오기 실패:", err);
    }
  };

  const handleEditEvent = (event) => {
    setSelectedEventId(event.id);
    setStartDate(event.startDateTime.slice(0, 10));
    setEndDate(event.endDateTime.slice(0, 10));
    setStartTime(`start-${dayjs(event.startDateTime).hour()}`);
    setEndTime(`end-${dayjs(event.endDateTime).hour()}`);
    setContent(event.content);
    setAllDay(event.allDay);
    setSelectedEvent(null); // 상세보기 닫기
    setIsModalOpen(true); // 작성/수정 팝업 열기
  };

  const handleDeleteEvent = async (id) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "일정 삭제",
      text: "정말 삭제하시겠습니까?",
      showCancelButton: true,
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${baseUrl}/api/schedules/${id}`);
      Swal.fire({
        icon: "success",
        title: "삭제 완료",
      });

      setSelectedEvent(null); // 팝업 닫기
      fetchEvents(); // 일정 목록 새로고침
    } catch (err) {
      console.error("삭제 실패:", err);
      Swal.fire({
        icon: "error",
        title: "삭제 실패",
        text: "서버 오류로 삭제할 수 없습니다.",
      });
    }
  };

  useEffect(() => {
    setDaysInMonth(getDaysInMonth(currentDate));
  }, [currentDate]);

  const goToPrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, "month"));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.add(1, "month"));
  };

  const today = dayjs().format("YYYY-MM-DD");

  return (
    <Container>
      <Header className="relative px-4 py-4 min-h-[10px]">
        <div className="absolute left-4 top-5"></div>

        {isAdmin && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <button
              className="px-3 py-1 text-sm bg-pink-400 text-white rounded"
              onClick={() => setIsModalOpen(true)}>
              일정등록
            </button>
          </div>
        )}

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-4">
          <NavButton onClick={goToPrevMonth}>
            <IoIosArrowBack size={30} />
          </NavButton>
          <TitleWrapper>
            <TitlePart>{currentDate.format("YYYY")}</TitlePart>
            <TitleDot>.</TitleDot>
            <TitlePart>{currentDate.format("MM")}</TitlePart>
          </TitleWrapper>
          <NavButton onClick={goToNextMonth}>
            <IoIosArrowForward size={30} />
          </NavButton>
        </div>

        <TodayButton
          className="px-4 py-1 text-sm whitespace-nowrap"
          onClick={() => setCurrentDate(dayjs())}>
          오늘
        </TodayButton>
      </Header>

      <DayWrapper>
        {weeks.map((week, index) => (
          <CalendarItem $isSunday={index === 0} key={week}>
            <span className="pc-only">{week}</span>
            <span className="mobile-only">{week[0]}</span>
          </CalendarItem>
        ))}
      </DayWrapper>

      <DayWrapper>
        {daysInMonth.map((date, index) => {
          const isToday = today === date.date;

          return (
            <Day
              key={index}
              $isCurrentMonth={date.month === currentDate.month()}
              $isToday={isToday}
              $isSunday={date.dayIndexOfWeek === 0}>
              {date.day && (
                <>
                  {/* 날짜 출력 */}
                  {isToday ? (
                    <DayCircle>{date.day}</DayCircle>
                  ) : (
                    <span>{date.day}</span>
                  )}

                  {/* ✅ 일정 출력 부분 */}
                  {events
                    .filter(
                      (event) =>
                        dayjs(event.startDateTime).format("YYYY-MM-DD") ===
                        date.date
                    )
                    .map((event) => (
                      <div
                        key={event.id}
                        onClick={() => setSelectedEvent(event)}
                        className="text-[11px] mt-1 bg-pink-100 text-pink-800 rounded px-1 py-[1px] cursor-pointer truncate"
                        title={event.content}>
                        {event.content.slice(0, 5)}
                      </div>
                    ))}
                </>
              )}
            </Day>
          );
        })}
      </DayWrapper>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white w-[700px] p-6 rounded shadow-md">
            {/* 상단 타이틀 + 닫기 */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">일정쓰기</h2>
              <button onClick={() => setIsModalOpen(false)}>✕</button>
            </div>

            {/* 날짜/시간 선택 줄 */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input
                type="date"
                className="border p-2 w-[150px]"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <select
                className="border p-2 w-[110px]"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}>
                {generateHours("start")}
              </select>
              <span className="text-sm">~</span>
              <input
                type="date"
                className="border p-2 w-[150px]"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <select
                className="border p-2 w-[110px]"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}>
                {generateHours("end")}
              </select>

              {/* <button
                className={`border p-2 text-sm whitespace-nowrap ${
                  allDay ? "bg-gray-300" : ""
                }`}
                onClick={toggleAllDay}>
                종일
              </button> */}
            </div>

            {/* 내용 입력 */}
            <textarea
              placeholder="내용을 입력해주세요. (최소 5자, 최대 500자)"
              className="border w-full h-40 p-3 text-sm"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={500}
            />

            {/* 하단 버튼 */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="border px-4 py-1 text-sm bg-gray-100"
                onClick={() => setIsModalOpen(false)}>
                취소
              </button>
              <button
                onClick={handleSubmitEvent}
                className="border px-4 py-1 text-sm bg-pink-400 text-white">
                등록
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[700px] max-w-[90%] rounded-lg shadow-lg p-8">
            <h2 className="text-xl font-bold mb-6">일정 상세</h2>

            <div className="mb-3 text-[15px] text-gray-800">
              <span className="font-semibold">날짜:</span>{" "}
              {selectedEvent.startDateTime.slice(0, 10)}{" "}
              {!selectedEvent.allDay &&
                `${selectedEvent.startDateTime.slice(
                  11,
                  16
                )} ~ ${selectedEvent.endDateTime.slice(11, 16)}`}
              {selectedEvent.allDay && " (종일)"}
            </div>

            <div className="mb-8 text-[15px] text-gray-800 whitespace-pre-line break-words">
              <span className="font-semibold">내용:</span>
              <br />
              {selectedEvent.content}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition"
                onClick={() => handleEditEvent(selectedEvent)}>
                수정
              </button>

              <button
                className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition"
                onClick={() => handleDeleteEvent(selectedEvent.id)}>
                삭제
              </button>

              <button
                className="px-4 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition"
                onClick={() => setSelectedEvent(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

function getDaysInMonth(date) {
  const startOfMonth = dayjs(date).startOf("month");
  const endOfMonth = dayjs(date).endOf("month");
  const days = [];

  const startDay = startOfMonth.day(); // 요일 index (0~6)

  for (let i = 0; i < startDay; i++) {
    days.push({ day: "", date: "", dayIndexOfWeek: i, month: -1 });
  }

  for (let d = 1; d <= endOfMonth.date(); d++) {
    const current = dayjs(date).date(d);
    days.push({
      day: d,
      date: current.format("YYYY-MM-DD"),
      dayIndexOfWeek: current.day(),
      month: current.month(),
    });
  }

  return days;
}

function generateHours(label) {
  const hours = [];
  for (let h = 0; h < 24; h++) {
    const hour = h % 12 === 0 ? 12 : h % 12;
    const period = h < 12 ? "오전" : "오후";
    const labelText = `${period} ${hour.toString().padStart(2, "0")}:00`;
    hours.push(
      <option key={`${label}-${h}`} value={`${label}-${h}`}>
        {labelText}
      </option>
    );
  }
  return hours;
}

export default Schedule;
